import { ApiResponse, PaginatedResponse } from "../types/common";
import {
  Branch,
  CreateBranchRequest,
  UpdateBranchRequest,
  BranchFilter,
} from "../types/branch";
import api from "./api";

// Branch CRUD Operations
export const getBranches = async (
  filters: BranchFilter = {}
): Promise<PaginatedResponse<Branch>> => {
  const params = new URLSearchParams();

  if (filters.page) params.append("page", filters.page.toString());
  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.search) params.append("search", filters.search);
  if (filters.status) params.append("status", filters.status);

  const response = await api.get(`/branches?${params.toString()}`);
  const data = response.data || response;

  // Ensure we return the correct structure for PaginatedResponse
  return {
    success: data.success || true,
    data: data.branches || data.data || [],
    pagination: {
      currentPage: data.currentPage || 1,
      totalPages: data.totalPages || 1,
      totalItems: data.total || 0,
      hasNext: (data.currentPage || 1) < (data.totalPages || 1),
      hasPrev: (data.currentPage || 1) > 1,
    },
  };
};

export const getBranchById = async (
  id: string
): Promise<ApiResponse<Branch>> => {
  const response = await api.get(`/branches/${id}`);
  return response.data || response;
};

export const createBranch = async (
  data: CreateBranchRequest
): Promise<ApiResponse<Branch>> => {
  const response = await api.post("/branches", data);
  return response.data || response;
};

export const updateBranch = async (
  id: string,
  data: UpdateBranchRequest
): Promise<ApiResponse<Branch>> => {
  const response = await api.put(`/branches/${id}`, data);
  return response.data || response;
};

export const deleteBranch = async (id: string): Promise<ApiResponse<void>> => {
  const response = await api.delete(`/branches/${id}`);
  return response.data || response;
};

// Branch Statistics
export const getBranchStats = async (
  branchId?: string
): Promise<ApiResponse<any>> => {
  try {
    const params = branchId ? `?branchId=${branchId}` : "";
    const response = await api.get(`/branches/stats${params}`);
    return response.data || response;
  } catch (error) {
    // Return mock data if stats endpoint doesn't exist
    console.warn("Branch stats endpoint not available, returning mock data");
    return {
      success: true,
      data: {
        totalBranches: 0,
        activeBranches: 0,
        inactiveBranches: 0,
        totalStudents: 0,
        totalTeachers: 0,
      },
      message: "Branch statistics (mock data)",
    };
  }
};

// Bulk Operations
export const bulkUpdateBranches = async (
  ids: string[],
  data: Partial<UpdateBranchRequest>
): Promise<ApiResponse<void>> => {
  const response = await api.put("/branches/bulk", { ids, data });
  return response.data || response;
};

export const bulkDeleteBranches = async (
  ids: string[]
): Promise<ApiResponse<void>> => {
  const response = await api.delete("/branches/bulk", { data: { ids } });
  return response.data || response;
};
